/**
 * LP promotional configuration
 *
 * Single source of truth for the promo copy, imagery, and colors on the landing
 * pages: /lp and /lp/cost (via getActiveLpPromoText), /men-lp (via
 * getCurrentVariantConfig), and the promo/google-v2 modules (via PROMO_ALT_TEXT).
 *
 * Rolling to a new promo is an edit to CURRENT_PROMO below and nothing else.
 * The offer line in particular is written once here and fanned out to the three
 * props S18VideoBackground needs, so the pages can't drift out of sync.
 */

import evergreenDesktop from '../images/lp/lp-evergreen-desktop.webp';
import evergreenMobile from '../images/lp/lp-evergreen-mobile.webp';

export interface PromoImages {
    mobile: string;
    desktop: string;
}

export interface HeroCopy {
    badge: string;
    eyebrow: string;
    subheading1: string;
    heading: string;
    subheading2: string;
    highlightWord?: string;
}

/**
 * Hero styling treatment, consumed by LandingPageHero. Seasonal promos (cyber week,
 * the holiday event, january) each had their own treatment; all of them expired in
 * Feb 2026 and only 'evergreen' is live today. Add a value here plus a matching
 * branch in LandingPageHero if a themed hero comes back.
 */
export type PromoVariant = 'evergreen';

interface PromoConfig {
    variant: PromoVariant;
    /** Headline offer. Underlined everywhere it renders. */
    offerLine: string;
    /** Line rendered directly beneath the offer. Empty string hides it. */
    bundleLine: string;
    /** Expiry line. `endsLineFirst` decides where it sits. */
    endsLine: string;
    /**
     * When true the expiry line leads the hero, above the offer; when false it sits
     * between the headline and the CTA. Drives /lp, /lp/cost and /men-lp together so
     * the three can't disagree about the order.
     */
    endsLineFirst: boolean;
    /** CTA button label. */
    ctaText: string;
    /** Eyebrow directly above the headline on /lp, /lp/cost and /men-lp. Empty string hides it. */
    eyebrow: string;
    /** Badge above the headline on the /men-lp hero. */
    badge: string;
    /** Hero background: a hex value, or a `bg-*` Tailwind class. */
    bgColor: string;
    /** Alt text for the standalone promo graphic in the promo/google-v2 modules. */
    altText: string;
    images: PromoImages;
}

const CURRENT_PROMO: PromoConfig = {
    variant: 'evergreen',
    offerLine: 'Up to 70% off',
    bundleLine: 'Plus, a limited-edition bundle',
    endsLine: 'Best of Fall Sale',
    endsLineFirst: true,
    ctaText: 'RESERVE MY SPOT',
    eyebrow: '',
    badge: 'Best offer of the season',
    bgColor: '#DDF4FF',
    altText: 'The Best of Fall Sale, up to 70% off all laser hair removal.',
    images: {
        mobile: evergreenMobile,
        desktop: evergreenDesktop,
    },
};

export const PROMO_ALT_TEXT = CURRENT_PROMO.altText;

interface HeroVariantConfig {
    variant: PromoVariant;
    copy: HeroCopy;
    images: PromoImages;
    subheading2First: boolean;
}

/**
 * Hero variant, copy, and imagery.
 * Used by: LandingPageHero, LandingPageHero.builder
 */
export function getCurrentVariantConfig(): HeroVariantConfig {
    const { variant, badge, eyebrow, offerLine, bundleLine, endsLine, endsLineFirst, images } = CURRENT_PROMO;

    return {
        variant,
        copy: {
            badge,
            eyebrow,
            // The hero renders `heading` above `subheading1`, so the offer takes the
            // headline and the bundle line sits beneath it.
            heading: offerLine,
            subheading1: bundleLine,
            subheading2: endsLine,
        },
        images,
        // The expiry line is subheading2, so leading with it means rendering that first.
        subheading2First: endsLineFirst,
    };
}

/**
 * Hero background color.
 * Used by: LandingPageHero, LandingPageHero.builder
 */
export function getActiveLpPromoBGColor(): string {
    return CURRENT_PROMO.bgColor;
}

interface LpPromoText {
    text: string;
    headlineText: string;
    graphicsHeadlineUnderlinedText: string;
    headlineSuffix: string;
    mobileUnderlinedLines: string[];
    preHeadlineText: string;
    buttonText: string;
    textAboveHeadline: boolean;
}

/**
 * Promo copy mapped onto S18VideoBackground's props.
 * Used by: /lp, /lp/cost
 */
export function getActiveLpPromoText(): LpPromoText {
    const { offerLine, bundleLine, endsLine, endsLineFirst, eyebrow, ctaText } = CURRENT_PROMO;

    return {
        // The desktop headline, the mobile headline, and the underline mask all need
        // the offer separately — derived here so they can't disagree.
        headlineText: offerLine,
        graphicsHeadlineUnderlinedText: offerLine,
        mobileUnderlinedLines: [offerLine],
        headlineSuffix: bundleLine,
        text: endsLine,
        preHeadlineText: eyebrow,
        buttonText: ctaText,
        // The expiry line maps onto S18VideoBackground's `text` prop.
        textAboveHeadline: endsLineFirst,
    };
}
